import {getTime, parseISO} from "date-fns";
import {format, getTimezoneOffset, toZonedTime} from "date-fns-tz";
import {Timezone} from "~~/package/mapper/common/Timezone";
import {useUserStore} from "~/store/user/userStore";

const convertToString = (number: number): string => {
  return number < 10 ? `0${number}` : `${number}`;
};

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December"
];

const year = (date: Date) => date.getFullYear();
const month = (date: Date) => convertToString(date.getMonth() + 1);
const day = (date: Date) => convertToString(date.getDate());
const hours = (date: Date) => convertToString(date.getHours());
const minutes = (date: Date) => convertToString(date.getMinutes());
const seconds = (date: Date) => convertToString(date.getSeconds());

// date-fns-tz 에 제대로 존재하지 않는 timezone 인 경우
const invalidTimezones: { [key: string]: string } = {
  'Pacific/Baker': '-12:00', // UM
  'Indian/Heard': '+05:00', // HM
  'Asia/Almaty': '+06:00', // KZ // 서머타임이 존재하지 않는데 offset 존재
  'Pacific/Honiara': '+11:00', // SB
  'Pacific/Tokelau': '+13:00' // TK
};

const dateToStringConvert = (date: Date, withDay = false): string => {
  let dateText = date.getFullYear() + "-" + ((date.getMonth() + 1) >= 10 ? (date.getMonth() + 1) : "0" + (date.getMonth() + 1));
  if (withDay) {
    dateText += "-" + (date.getDate() >= 10 ? date.getDate() : "0" + date.getDate());
  }
  return dateText;
};

const getUTCDate = (date: string): Date => {
  const dateText = date.toUpperCase().includes("Z") ? date! : date! + "Z";
  return new Date(dateText);
};

const convertTimeToUSFormatString = (timestamp: string | number) => {
  const date = new Date(timestamp);

  return `${monthNames[date.getMonth()]} ${day(date)}, ${year(date)}, ${hours(
    date
  )}:${minutes(date)}:${seconds(date)} (UTC)`;
};

const convertTimeToShortLocalFormatString = (timestamp: string | number, locale: string, isWithDay = false) => {
  const options: {[key:string]: string} = {
    year: "numeric",
    month: "long"
  };

  if(isWithDay) options.day = "numeric";

  return timestamp !== null && timestamp !== undefined ?
    new Date(timestamp).toLocaleDateString(locale, options) :
    "";
};

const convertTimeToLocalFormatString = (timestamp: string | number, locale: string) => {
  return timestamp !== null && timestamp !== undefined ?
    new Date(timestamp).toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric"
    }) :
    "";
};

const convertUTCTimeToLocalTime = (date: string) => {
  const utcDate = parseISO(date + "Z");
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const localDateString = format(utcDate, "yyyy-MM-dd HH:mm:ssXXX", { timeZone });
  return getTime(new Date(localDateString));
};

const getTimeZoneDateString = (timestamp: string | number, timeZone = "") => {
  let utcDate: Date;
  if (typeof timestamp === "string") utcDate = parseISO(timestamp + "Z");
  else utcDate = new Date(timestamp);

  if (isNaN(utcDate.getTime())) {
    console.warn("⛔ Invalid timestamp in getTimeZoneDateString:", timestamp);
    return "";
  }

  // UTC -> timezone 적용 시간으로 변경
  if (invalidTimezones[locationToTimezone(timeZone)]) {
    const offsetDate = getDateWithOffset(utcDate, locationToTimezone(timeZone), false);
    return offsetDate.toISOString().slice(0, -5)
  }
  else return format(toZonedTime(utcDate, locationToTimezone(timeZone)), "yyyy-MM-dd'T'HH:mm:ss");
}

const calculateDateStringWith = (date: string, time: string, timeZone = ""): string => {
  const [hours, minutes] = time.split(":").map(String);

  const dayAndTime = date + "T" + hours.padStart(2, "0") + ":" + minutes.padStart(2, "0");

  timeZone = locationToTimezone(timeZone);

  // 입력값 -> 지정 timezone 을 적용한 UTC 시간으로 변경
  if (invalidTimezones[timeZone]) {
    const offsetDate = getDateWithOffset(parseISO(dayAndTime + "Z"), timeZone, true);
    return format(offsetDate, "yyyy-MM-dd'T'HH:mm:ssXXX");
  }
  else return format(dayAndTime, "yyyy-MM-dd'T'HH:mm:ssXXX", { timeZone });
};

const getDateWithOffset = (date: Date, timeZone: string, isConvertToUTC: boolean) => {
  const offset = getTimezoneOffset(invalidTimezones[timeZone]);

  const offsetDate = new Date(offset * (isConvertToUTC ? -1 : 1) + date.getTime());

  return offsetDate;
}

const calculateDateToKSTStringWith = (currentDate: string): string => {
  return format(currentDate, "yyyy-MM-dd'T'HH:mm", { timeZone: 'Asia/Seoul' });
};

const locationToTimezone = (countryCode = "") => {
  // location(국가코드 -> timezone)
  if (!useUserStore().isLogin()) return Intl.DateTimeFormat().resolvedOptions().timeZone;

  let timezoneKey = (useUserStore().userV2.location ?? "").slice(0, -1).split('(')[1];
  if (countryCode) timezoneKey = countryCode;
  // 프로필 국가가 비었거나 "South Korea (KR)" 형식이 아니면 표에서 못 찾는다 → 브라우저 시간대로 대체
  return Timezone[timezoneKey as keyof typeof Timezone] ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const getDaylightOffset = (timeZone: string) => {
  if (invalidTimezones[timeZone]) return 0; // date-fns-tz 에 제대로 존재하지 않는 timezone 인 경우 모두 서머타임 없음

  const januaryOffset = getTimezoneOffset(timeZone, new Date('2024-01-01T00:00:00Z')); // 1월 1일
  const julyOffset = getTimezoneOffset(timeZone, new Date('2024-07-01T00:00:00Z')); // 7월 1일

  if (januaryOffset === julyOffset) return 0;

  if (januaryOffset < julyOffset) return julyOffset; // 북반구 서머타임: 7월(1월이 더 작은 경우)
  else return januaryOffset; // 남반구 서머타임: 1월(7월이 더 작은 경우)
}

const getDaylightOffsetStringForTimeZone = (timeZone: string) => {
  // 라이브러리 데이터 이슈 (standard offset: -02:00, daylight offset: -01:00)
  if (timeZone === 'America/Nuuk') return '-02:00'; // 올바른 값 (standard offset: -03:00, daylight offset: -02:00)
  else return convertOffsetToString(getDaylightOffset(timeZone));
}

const convertOffsetToString = (offset: number) => {
  if (!offset) return '+00:00';

  // 밀리초를 절대값으로 변환하여 시간과 분 계산
  const sign = offset < 0 ? '-' : '+';
  const totalMinutes = Math.abs(offset) / (1000 * 60);
  const hours = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
  const minutes = Math.floor(totalMinutes % 60).toString().padStart(2, '0');

  return `${sign}${hours}:${minutes}`;
}

export {
  dateToStringConvert,
  getUTCDate,
  convertTimeToUSFormatString,
  convertTimeToShortLocalFormatString,
  convertTimeToLocalFormatString,
  convertUTCTimeToLocalTime,
  calculateDateStringWith,
  calculateDateToKSTStringWith,
  getTimeZoneDateString,
  getDaylightOffset,
  getDaylightOffsetStringForTimeZone
};
